/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import {
  getBraindatePath,
  getBraindateConversation,
} from '@e180/domain/lib/braindate/util';
import { getConversationParticipants } from '@e180/domain/lib/conversation/util';
import { getUserId, getUserFirstName } from '@e180/domain/lib/user/util';

import ConversationAvatar from 'src/shared/app/conversation/component/avatar/ConversationAvatar';
import Notification from 'src/shared/app/notification/component/Notification';
import messages from 'src/shared/app/notification/l10n/notificationL10n';
import { getNotificationData } from 'src/shared/app/notification/util/notificationUtil';
import UserAvatar, {
  small,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { User } from '@e180/domain/lib/user/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

type Props = {
  notification: NotificationType,
  user: User,
  handleClick: () => void,
  handleDismiss: () => void,
  // Self passed
  intl: intlShape,
};

const CheckInNotificationDumb = ({
  notification,
  user,
  handleClick,
  handleDismiss,
  intl,
}: Props): React.Node => {
  // $FlowIssue
  const braindate: Braindate = getNotificationData(notification);
  const href = getBraindatePath(braindate);
  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);
  const participants = getConversationParticipants(conversation);
  const otherParticipants = participants.filter(
    (p) => getUserId(p) !== getUserId(user),
  );

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const { braindateSingular } = useEventBraindateName();

  const messageTxt = intl.formatMessage(messages.checkIn, {
    isGroupBraindate,
    firstName: otherParticipants.map(getUserFirstName).join(', '),
    braindateSingular,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const imageElt = isGroupBraindate ? (
    <ConversationAvatar conversation={conversation} />
  ) : (
    <UserAvatar user={otherParticipants[0]} variant={small} />
  );

  return (
    <Notification
      href={href}
      media={imageElt}
      handleClick={handleClick}
      handleDismiss={handleDismiss}
    >
      {messageTxt}
    </Notification>
  );
};

export default compose(React.memo, injectIntl)(CheckInNotificationDumb);
